/**
 * API Key Input Modal
 * Generic modal for entering API keys or other credentials
 * Used when MCP servers require api_key authentication
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Key, Eye, EyeOff, AlertCircle } from 'lucide-react';

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (credentials: Record<string, string>) => Promise<void>;
  serverName: string;
  requiredFields?: string[];
  description?: string;
}

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  serverName,
  requiredFields = ['api_key'],
  description
}) => {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate all required fields are filled
    const missingFields = requiredFields.filter(field => !credentials[field]?.trim());
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    try {
      setLoading(true);
      await onSubmit(credentials);
      // Clear form and close on success
      setCredentials({});
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleShowValue = (field: string) => {
    setShowValues(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getFieldLabel = (field: string): string => {
    // Convert field name to user-friendly label
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getFieldPlaceholder = (field: string): string => {
    // Provide helpful placeholders based on field name
    const placeholders: Record<string, string> = {
      api_key: 'Enter your API key',
      secret_key: 'Enter your secret key',
      access_token: 'Enter your access token',
      client_id: 'Enter your client ID',
      client_secret: 'Enter your client secret',
      username: 'Enter your username',
      password: 'Enter your password',
    };
    return placeholders[field] || `Enter ${getFieldLabel(field).toLowerCase()}`;
  };

  const isSecretField = (field: string): boolean => {
    // Determine if field should be masked
    const secretFields = ['api_key', 'secret', 'password', 'token', 'credential'];
    return secretFields.some(secret => field.toLowerCase().includes(secret));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Authentication Required
          </DialogTitle>
          <DialogDescription>
            {description || `Enter your credentials for ${serverName}`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {requiredFields.map(field => {
            const isSecret = isSecretField(field);
            const showValue = !isSecret || showValues[field];
            
            return (
              <div key={field} className="space-y-2">
                <Label htmlFor={field}>
                  {getFieldLabel(field)}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id={field}
                    type={showValue ? 'text' : 'password'}
                    value={credentials[field] || ''}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    placeholder={getFieldPlaceholder(field)}
                    className="pr-10"
                    disabled={loading}
                    required
                  />
                  {isSecret && (
                    <button
                      type="button"
                      onClick={() => toggleShowValue(field)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showValue ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Authenticating...' : 'Authenticate'}
            </Button>
          </DialogFooter>
        </form>

        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong>Security Note:</strong> Your credentials are encrypted and stored securely. 
            They are only used to authenticate with {serverName}.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeyModal;